"use client";

import { useState } from "react";
import {
  addGoalMilestone,
  createGoal,
  createTask,
  getGoalDraft,
} from "@/lib/api";
import type { GoalDraft } from "@/lib/api";
import { describeGoalDraftExecutionStyle } from "@/lib/goalCadence";

interface Props {
  onClose: () => void;
  onCreated: () => Promise<void> | void;
  onNavigate: (tab: string) => void;
}

function buildTargetDate(targetWeeks: number): string {
  const date = new Date();
  date.setDate(date.getDate() + targetWeeks * 7);
  return date.toISOString();
}

function compactDraftSummary(summary?: string | null, description?: string | null, maxChars: number = 110): string {
  const source = (summary || description || "").replace(/\s+/g, " ").trim();
  if (!source) return "Shape this into a clearer goal and first step.";
  if (source.length <= maxChars) return source;
  const clipped = source.slice(0, maxChars).split(" ");
  clipped.pop();
  return `${(clipped.join(" ").trim() || source.slice(0, maxChars).trim()).replace(/[ ,.;:]+$/, "")}…`;
}

export default function GoalStudio({ onClose, onCreated, onNavigate }: Props) {
  const [text, setText] = useState("");
  const [draft, setDraft] = useState<GoalDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleDraft = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const nextDraft = await getGoalDraft(trimmed);
      setDraft(nextDraft);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to analyse goal");
    } finally {
      setLoading(false);
    }
  };

  const persistGoal = async (mode: "save" | "save_task") => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const goal = await createGoal({
        title: draft.title,
        description: draft.description,
        category: draft.category || undefined,
        target_date: buildTargetDate(draft.target_weeks),
        motivation: draft.motivation,
      });

      for (const milestone of draft.milestones) {
        await addGoalMilestone(goal.id, milestone);
      }

      if (mode === "save_task") {
        await createTask({
          title: draft.first_action,
          description: `Starter action for ${draft.title}`,
          goal_id: goal.id,
          priority: 2,
          energy_level: "medium",
          estimated_minutes: draft.suggested_session_minutes,
          tags: draft.category ? [draft.category] : [],
        });
      }

      await onCreated();

      if (mode === "save_task") {
        setSuccess("Goal created with the first session queued. Open Schedule when you're ready to place it.");
        setText("");
        setDraft(null);
      } else {
        setSuccess("Goal created. Review the roadmap first, then place the first session when it truly fits.");
        setText("");
        setDraft(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create goal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-[28px] border border-border p-5 shadow-2xl">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 id="goal-studio-title" className="text-base font-semibold text-text">AI Goal Studio</h2>
          <p className="text-sm text-muted mt-1 leading-relaxed">
            Dump the messy version if you want. DecisionEase will refine it into a clear goal, roadmap, and first action.
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-xs text-muted hover:text-text transition-colors"
        >
          Close
        </button>
      </div>

      <div className="rounded-2xl bg-bg border border-border/80 p-3 mb-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Example: I have been thinking a lot about learning piano, making time for practice, and maybe building a calm weekly habit before my trip."
          className="w-full bg-transparent outline-none resize-none text-sm text-text placeholder:text-muted"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={handleDraft}
          disabled={loading || !text.trim()}
          className="h-10 rounded-full bg-forest px-4 text-sm font-medium text-white disabled:opacity-50"
        >
          {loading ? "Refining..." : "Refine goal"}
        </button>
        <button
          onClick={() => {
            setText("");
            setDraft(null);
            setError(null);
            setSuccess(null);
          }}
          className="h-10 rounded-full bg-secondary px-4 text-sm font-medium text-text-light"
        >
          Reset
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-2xl border border-rose/20 bg-rose/10 px-4 py-3 text-sm text-rose">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 rounded-2xl border border-accent/20 bg-accent/10 px-4 py-3 text-sm text-accent">
          <div className="flex items-center justify-between gap-3">
            <span>{success}</span>
            <button
              onClick={() => onNavigate("schedule")}
              className="text-xs font-medium underline"
            >
              Open timetable
            </button>
          </div>
        </div>
      )}

      {draft && (
        <div className="space-y-4">
          {(() => {
            const executionStyle = describeGoalDraftExecutionStyle(draft);
            return (
          <div className="rounded-2xl bg-sage-pale/30 border border-border/70 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Refined goal</p>
                <p className="text-lg font-semibold text-text">{draft.title}</p>
                <p className="text-sm text-muted mt-1">{compactDraftSummary(draft.summary, draft.description)}</p>
                <p className="mt-2 text-[11px] leading-relaxed text-text-light">{executionStyle.hint}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {draft.category && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-card text-forest">
                    {draft.category}
                  </span>
                )}
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-card text-blue">
                  {executionStyle.label}
                </span>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-card text-blue">
                  {draft.target_weeks} week plan
                </span>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-card text-amber">
                  {executionStyle.cadenceChip || `${draft.suggested_session_minutes}m sessions`}
                </span>
              </div>
            </div>
          </div>
            );
          })()}

          <div className="rounded-2xl bg-bg border border-border/70 p-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted mb-2">AI read</p>
            <p className="text-sm text-text leading-relaxed">{draft.description}</p>
            <p className="text-sm text-text-light leading-relaxed mt-3">{draft.analysis}</p>
            <p className="text-sm text-text-light leading-relaxed mt-3">{draft.motivation}</p>
          </div>

          <div className="rounded-2xl bg-bg border border-border/70 p-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted mb-2">Roadmap</p>
            <div className="space-y-2">
              {draft.milestones.map((milestone, index) => (
                <div key={milestone} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-forest text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-sm text-text-light">{milestone}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-blue/10 border border-blue/15 p-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-blue mb-2">First action</p>
            <p className="text-sm text-text leading-relaxed">{draft.first_action}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => persistGoal("save")}
              disabled={saving}
              className="h-10 rounded-full bg-secondary px-4 text-sm font-medium text-text-light disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save goal only"}
            </button>
            <button
              onClick={() => persistGoal("save_task")}
              disabled={saving}
              className="h-10 rounded-full bg-forest px-4 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? "Saving..." : "Create + starter task"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
